/**
 * Language codes and message-key domain taxonomy for the i18n catalog
 * (A09A — domain-barrel/generator/census infrastructure; no product
 * strings move as part of this module).
 *
 * A message key's *domain* is its first dot-separated namespace segment
 * (e.g. "ribbon.tab.file" -> domain "ribbon"; "prompt.line.first" ->
 * domain "prompt"). This mirrors the naming convention documented on
 * `./messages.ts` and is the seam a future domain-barrel split (see
 * `./generateMessageBarrels.ts`) partitions the catalog along.
 *
 * `LanguageCode` / `SUPPORTED_LANGUAGES` / `DEFAULT_LANGUAGE` live here
 * (moved out of `./messages.ts`, which re-exports them unchanged so no
 * external import path changes) because they are catalog-wide taxonomy,
 * not RU/EN product content.
 */

export type LanguageCode = "ru" | "en";

export const SUPPORTED_LANGUAGES: LanguageCode[] = ["ru", "en"];
export const DEFAULT_LANGUAGE: LanguageCode = "ru";

/** Domain bucket for a key with no dot-separated namespace segment. */
export const ROOT_MESSAGE_DOMAIN = "root";

/**
 * Returns the domain (first dot-segment) of a dot-separated message key.
 * Pure and total: every key maps to exactly one domain, so grouping by
 * this function can never leave a key unassigned or assigned twice.
 */
export function domainOfMessageKey(key: string): string {
  const dot = key.indexOf(".");
  return dot === -1 ? ROOT_MESSAGE_DOMAIN : key.slice(0, dot);
}

/**
 * Groups keys by domain, preserving each key's original relative order
 * within its domain bucket. Map iteration order follows the first
 * occurrence of each domain in `keys`.
 */
export function groupMessageKeysByDomain<K extends string>(
  keys: readonly K[],
): Map<string, K[]> {
  const byDomain = new Map<string, K[]>();
  for (const key of keys) {
    const domain = domainOfMessageKey(key);
    const bucket = byDomain.get(domain);
    if (bucket) {
      bucket.push(key);
    } else {
      byDomain.set(domain, [key]);
    }
  }
  return byDomain;
}
